import React, { useMemo, useState, useEffect } from "react";
import PageHeader from "@/components/Common/PageHeader";
import { ChevronDown, ArrowLeft, Save, Trash2, Calendar } from "lucide-react";
import Dropdown from "@/components/ui/Dropdown";
import useToastStore from "@/components/Common/Toast/toastStore";
import { InterViewSlotService } from "@/services/InterViewSlot";
import { companiesService } from "@/services/companiesService";
import { vacancyService } from "@/services/vacancyService";
import { useNavigate } from "react-router-dom";
import { stageService } from "@/services/stageService";
import DatePicker from "react-datepicker";
import { v4 as uuidv4 } from "uuid"; // Ensure uuid is imported if using it, or use crypto.randomUUID
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

const CreateEventSlot = () => {
  const { showToast } = useToastStore;
  const navigate = useNavigate();

  const [createSlotForm, setCreateSlotForm] = useState({
    selectedCompany: "",
    selectedVacancy: "",
    selectedStatus: "",
  });

  const [scheduleList, setScheduleList] = useState([]);

  // Bulk Selection State
  const [selectedRows, setSelectedRows] = useState([]);

  // Bulk Edit State
  const [bulkEditForm, setBulkEditForm] = useState({
    fromTime: "",
    toTime: "",
    interviewType: "",
    slotDuration: "",
    breakDuration: "",
  });

  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);

  const [isCreatingSlot, setIsCreatingSlot] = useState(false);

  const dateRangeLabel = useMemo(() => {
    const [start, end] = selectedDateRange;
    if (!start && !end) return "";
    if (start && end)
      return `${format(start, "dd/MM/yyyy")} - ${format(end, "dd/MM/yyyy")}`;
    if (start) return `${format(start, "dd/MM/yyyy")} -`;
    return "";
  }, [selectedDateRange]);
  const [companies, setCompanies] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [isFetchingVacancies, setIsFetchingVacancies] = useState(false);
  const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);

  const slotDurationOptions = [15, 30, 45, 60];
  const breakDurationOptions = [0, 5, 10, 15, 20, 30, 45, 60];
  const interviewTypeOptions = [
    { value: "Phone Interview", label: "Phone Interview", duration: 15 },
    { value: "Video Interview", label: "Video Interview", duration: 30 },
  ];

  const getDurationByInterviewType = (type) => {
    const match = interviewTypeOptions.find((o) => o.value === type);
    return match ? String(match.duration) : "";
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await companiesService.getCompaniesList({ all: true });
      setCompanies(response.companies || response.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      showToast({ message: "Failed to fetch companies", type: "error" });
    }
  };

  const fetchVacancies = async (companyId) => {
    if (!companyId) {
      setVacancies([]);
      return;
    }
    setIsFetchingVacancies(true);
    try {
      // Filter vacancies by status 0 (Active) and 1 (Closed/OnHold? - verifying requirements: status[]=0&status[]=1)
      const response = await vacancyService.getVacancies(companyId, {
        status: [0, 1],
      });
      setVacancies(response.vacancies || response.data || []);
    } catch (error) {
      console.error("Error fetching vacancies:", error);
      showToast({ message: "Failed to fetch vacancies", type: "error" });
      setVacancies([]);
    } finally {
      setIsFetchingVacancies(false);
    }
  };

  const fetchStatuses = async (vacancyId) => {
    if (!vacancyId) {
      setStatuses([]);
      return;
    }
    setIsFetchingStatuses(true);
    try {
      const response = await stageService.getVacancyStages(vacancyId);
      // API returns the array directly or in response.data
      setStatuses(
        Array.isArray(response)
          ? response
          : response.statuses || response.data || [],
      );
    } catch (error) {
      console.error("Error fetching statuses:", error);
      showToast({ message: "Failed to fetch statuses", type: "error" });
      setStatuses([]);
    } finally {
      setIsFetchingStatuses(false);
    }
  };

  const handleCreateSlotFormChange = (field, value) => {
    setCreateSlotForm((prev) => {
      const newData = { ...prev, [field]: value };

      // Fetch vacancies if company changes
      if (field === "selectedCompany") {
        fetchVacancies(value);
        newData.selectedVacancy = "";
        newData.selectedStatus = "";
        setStatuses([]);
      }

      // Fetch statuses if vacancy changes
      if (field === "selectedVacancy") {
        fetchStatuses(value);
        newData.selectedStatus = "";
      }

      return newData;
    });
  };

  const handleDateRangeSelect = (val) => {
    setSelectedDateRange(val);
  };

  const handleCreateAvailability = () => {
    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      showToast({ message: "Please select a date range.", type: "warning" });
      return;
    }
    console.log("start date", startDate);

    let diffDays = 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const newSchedules = [];

    for (let i = 0; i <= diffDays; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = format(date, "yyyy-MM-dd");

      newSchedules.push({
        id: uuidv4(),
        date: dateStr,
        fromTime: "",
        toTime: "",
        interviewType: "",
        slotDuration: "",
        breakDuration: "",
      });
    }

    if (newSchedules.length > 0) {
      setScheduleList((prev) => [...prev, ...newSchedules]);
      showToast({
        message: `Added ${newSchedules.length} slots.`,
        type: "success",
      });
      // Optional: Clear selection after adding
      // setSelectedDateRange([null, null]);
    }
  };

  // -- Bulk Actions --

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(scheduleList.map((s) => s.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        return prev.filter((rowId) => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleBulkEditChange = (field, value) => {
    if (field === "interviewType") {
      const autoDuration = getDurationByInterviewType(value);
      setBulkEditForm((prev) => ({
        ...prev,
        interviewType: value,
        slotDuration: autoDuration || "",
      }));
      return;
    }
    setBulkEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyBulkEdit = () => {
    if (selectedRows.length === 0) {
      showToast({ message: "No rows selected.", type: "warning" });
      return;
    }

    setScheduleList((prev) =>
      prev.map((item) => {
        if (selectedRows.includes(item.id)) {
          const updates = {};
          if (bulkEditForm.fromTime) updates.fromTime = bulkEditForm.fromTime;
          if (bulkEditForm.toTime) updates.toTime = bulkEditForm.toTime;
          if (bulkEditForm.interviewType) {
            updates.interviewType = bulkEditForm.interviewType;
            const autoDuration = getDurationByInterviewType(
              bulkEditForm.interviewType,
            );
            if (autoDuration) updates.slotDuration = autoDuration;
          } else if (bulkEditForm.slotDuration) {
            updates.slotDuration = bulkEditForm.slotDuration;
          }
          if (bulkEditForm.breakDuration !== "")
            updates.breakDuration = bulkEditForm.breakDuration;

          return { ...item, ...updates };
        }
        return item;
      }),
    );

    showToast({ message: "Updated selected rows.", type: "success" });
  };

  const deleteSelectedRows = () => {
    if (selectedRows.length === 0) return;
    setScheduleList((prev) =>
      prev.filter((item) => !selectedRows.includes(item.id)),
    );
    setSelectedRows([]);
  };

  const handleScheduleChange = (index, field, value) => {
    setScheduleList((prev) => {
      const newList = [...prev];
      if (field === "interviewType") {
        const autoDuration = getDurationByInterviewType(value);
        newList[index] = {
          ...newList[index],
          interviewType: value,
          slotDuration: autoDuration || "",
        };
      } else {
        newList[index] = { ...newList[index], [field]: value };
      }
      return newList;
    });
  };

  const removeSchedule = (index) => {
    const idToRemove = scheduleList[index].id;
    setScheduleList((prev) => prev.filter((_, i) => i !== index));
    setSelectedRows((prev) => prev.filter((id) => id !== idToRemove));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const validateTimeSlot = (startTime, endTime) => {
    if (!startTime || !endTime) return false;
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes > startMinutes;
  };

  const handleCreateSlot = async () => {
    const { selectedCompany, selectedVacancy, selectedStatus } = createSlotForm;

    // Validation
    if (!selectedCompany) {
      showToast({ message: "Please select a company.", type: "warning" });
      return;
    }
    if (!selectedVacancy) {
      showToast({ message: "Please select a vacancy.", type: "warning" });
      return;
    }
    if (!selectedStatus) {
      showToast({ message: "Please select a status.", type: "warning" });
      return;
    }

    if (scheduleList.length === 0) {
      showToast({
        message: "Please add at least one schedule.",
        type: "warning",
      });
      return;
    }

    const errors = [];
    scheduleList.forEach((schedule, index) => {
      const { date, fromTime, toTime, slotDuration, breakDuration } = schedule;

      if (!fromTime || !toTime || !slotDuration) {
        // breakDuration is optional in some contexts but required by UI validation previously.
        // The new validation says 'sometimes|nullable'.
        // However, UI has a dropdown with options, suggesting it should be selected.
        // I will keep enforcing breakDuration in UI for consistency, but payload handles it.
        if (!breakDuration && breakDuration !== 0) {
          errors.push(`Please fill all fields for ${schedule.date}`);
          return;
        }
      }
      if (!fromTime || !toTime || !slotDuration) {
        errors.push(`Please fill all fields for ${schedule.date}`);
        return;
      }

      if (!validateTimeSlot(fromTime, toTime)) {
        errors.push(`Invalid time range for ${schedule.date}`);
        return;
      }
    });

    if (errors.length > 0) {
      showToast({ message: errors[0], type: "error" });
      return;
    }

    setIsCreatingSlot(true);
    try {
      const payload = {
        company_id: selectedCompany,
        vacancy_id: selectedVacancy,
        vacancy_status_id: selectedStatus,
        availabilities: scheduleList.map((schedule) => ({
          date: schedule.date,
          start_time: `${schedule.date} ${schedule.fromTime}:00`,
          end_time: `${schedule.date} ${schedule.toTime}:00`,
          slot_duration: parseInt(schedule.slotDuration),
          break_duration: parseInt(schedule.breakDuration) || 0,
          ...(schedule.interviewType
            ? { interview_type: schedule.interviewType }
            : {}),
        })),
      };

      await InterViewSlotService.createSlots(payload);
      showToast({
        message: "Event slots created successfully!",
        type: "success",
      });

      // Navigate back to the list page
      navigate("/interviews-slots");
    } catch (error) {
      showToast({
        message:
          error.message || "Failed to create event slots. Please try again.",
        type: "error",
      });
    } finally {
      setIsCreatingSlot(false);
    }
  };

  return (
    <div className="py-6 px-8 w-[90%] mx-auto">
      <PageHeader
        navigationText="Back to Interview Slots"
        navigateTo={() => navigate("/interviews-slots")}
        navButtonClass="flex items-center gap-2 px-4 py-2 bg-primary-dark hover:bg-primary-dark text-white font-medium rounded-lg transition-colors text-sm group w-fit"
      />
      <div className="mb-8">
        <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
          Interview Slots
        </h1>
        <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
          Set interview availability, manage booking windows, and keep
          scheduling organised for your team.
        </p>
      </div>

      <div className="space-y-6  mx-auto">
        {/* 1. Context Selection */}
        {/* Context Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Select Company Card */}
          <div className="self-stretch min-w-80 p-5 relative bg-card rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] border border-border flex flex-col justify-start items-start gap-5">
            <div className="w-full flex flex-col justify-start items-start gap-1.5">
              <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                <div className="self-stretch flex justify-between items-center">
                  <div className="inline-flex justify-start items-center gap-1">
                    <div className="text-muted-foreground text-sm font-medium font-['Inter'] leading-5">
                      Select Company
                    </div>
                    <div className="text-foreground text-sm font-medium font-['Inter'] leading-5">
                      *
                    </div>
                    <div className="w-4 h-4 text-muted-foreground">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-full h-full"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    </div>
                  </div>
                  <div className="w-5 h-5 text-muted-foreground">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-full h-full"
                    >
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </div>
                </div>
                <Dropdown
                  options={companies.map((company) => ({
                    label: company.name,
                    value: company.id,
                  }))}
                  value={
                    companies
                      .map((company) => ({
                        label: company.name,
                        value: company.id,
                      }))
                      .find(
                        (opt) => opt.value === createSlotForm.selectedCompany,
                      ) || null
                  }
                  onChange={(option) =>
                    handleCreateSlotFormChange("selectedCompany", option.value)
                  }
                  placeholder="Select Company"
                  searchable
                  className="w-full"
                  triggerClassName="!h-11 !px-3 !py-2 !bg-card !rounded-lg !border-input shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
                  customIcon={
                    <div className="w-2 h-2 mr-2 bg-[#17B26A] rounded-full" />
                  }
                />
              </div>
            </div>
          </div>

          {/* Select Vacancy Card */}
          <div className="self-stretch min-w-80 p-5 relative bg-card rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] border border-border flex flex-col justify-start items-start gap-5">
            <div className="w-full flex flex-col justify-start items-start gap-1.5">
              <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                <div className="self-stretch flex justify-between items-center">
                  <div className="inline-flex justify-start items-center gap-1">
                    <div className="text-muted-foreground text-sm font-medium font-['Inter'] leading-5">
                      Select Vacancy
                    </div>
                    <div className="text-foreground text-sm font-medium font-['Inter'] leading-5">
                      *
                    </div>
                    <div className="w-4 h-4 text-muted-foreground">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-full h-full"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    </div>
                  </div>
                  <div className="w-5 h-5 text-muted-foreground">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-full h-full"
                    >
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </div>
                </div>
                <Dropdown
                  options={vacancies.map((v) => ({
                    label: v.title,
                    value: v.id,
                  }))}
                  value={
                    vacancies
                      .map((v) => ({ label: v.title, value: v.id }))
                      .find(
                        (opt) => opt.value === createSlotForm.selectedVacancy,
                      ) || null
                  }
                  onChange={(option) =>
                    handleCreateSlotFormChange("selectedVacancy", option.value)
                  }
                  placeholder={
                    isFetchingVacancies ? "Loading..." : "Select Vacancy"
                  }
                  searchable
                  className="w-full"
                  disabled={
                    !createSlotForm.selectedCompany || isFetchingVacancies
                  }
                  triggerClassName="!h-11 !px-3 !py-2 !bg-card !rounded-lg !border-input shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
                  customIcon={
                    <div className="w-2 h-2 mr-2 bg-[#17B26A] rounded-full" />
                  }
                />
              </div>
            </div>
          </div>

          {/* Select Status Card */}
          <div className="self-stretch min-w-80 p-5 relative bg-card rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] border border-border flex flex-col justify-start items-start gap-5">
            <div className="w-full flex flex-col justify-start items-start gap-1.5">
              <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                <div className="self-stretch flex justify-between items-center">
                  <div className="inline-flex justify-start items-center gap-1">
                    <div className="text-muted-foreground text-sm font-medium font-['Inter'] leading-5">
                      Select Status
                    </div>
                    <div className="text-foreground text-sm font-medium font-['Inter'] leading-5">
                      *
                    </div>
                    <div className="w-4 h-4 text-muted-foreground">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-full h-full"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    </div>
                  </div>
                  <div className="w-5 h-5 text-muted-foreground">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-full h-full"
                    >
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </div>
                </div>
                <Dropdown
                  options={statuses.map((s) => ({
                    label: s.name,
                    value: s.vacancy_status_id || s.id,
                  }))}
                  value={
                    statuses
                      .map((s) => ({
                        label: s.name,
                        value: s.vacancy_status_id || s.id,
                      }))
                      .find(
                        (opt) => opt.value === createSlotForm.selectedStatus,
                      ) || null
                  }
                  onChange={(option) =>
                    handleCreateSlotFormChange("selectedStatus", option.value)
                  }
                  placeholder={
                    isFetchingStatuses ? "Loading..." : "Select Status"
                  }
                  searchable
                  className="w-full"
                  disabled={
                    !createSlotForm.selectedVacancy || isFetchingStatuses
                  }
                  triggerClassName="!h-11 !px-3 !py-2 !bg-card !rounded-lg !border-input shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
                  customIcon={
                    <div className="w-2 h-2 mr-2 bg-[#17B26A] rounded-full" />
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          {/* Bulk Actions (Visible if rows exist) */}
          {scheduleList.length > 0 && (
            <div className="sticky top-0 mb-4 bg-card rounded-xl shadow-sm border border-border pt-3 p-0">
              <div className="flex items-center justify-between mb-2 px-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Bulk Edit
                </h3>
                <div className="text-sm text-muted-foreground">
                  {selectedRows.length} row(s) selected
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end bg-muted p-4 rounded-lg">
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-muted-foreground">
                    From Time
                  </label>
                  <input
                    type="time"
                    value={bulkEditForm.fromTime}
                    onChange={(e) =>
                      handleBulkEditChange("fromTime", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-card text-foreground"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-muted-foreground">
                    To Time
                  </label>
                  <input
                    type="time"
                    value={bulkEditForm.toTime}
                    onChange={(e) =>
                      handleBulkEditChange("toTime", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-card text-foreground"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Interview Type
                  </label>
                  <select
                    value={bulkEditForm.interviewType}
                    onChange={(e) =>
                      handleBulkEditChange("interviewType", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-card text-foreground"
                  >
                    <option value="">Select</option>
                    {interviewTypeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Slot Duration
                  </label>
                  <select
                    value={bulkEditForm.slotDuration}
                    onChange={(e) =>
                      handleBulkEditChange("slotDuration", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-card text-foreground"
                  >
                    <option value="">Select</option>
                    {slotDurationOptions.map((duration) => (
                      <option key={duration} value={duration}>
                        {duration} Min
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Break Duration
                  </label>
                  <select
                    value={bulkEditForm.breakDuration}
                    onChange={(e) =>
                      handleBulkEditChange("breakDuration", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-card text-foreground"
                  >
                    <option value="">Select</option>
                    {breakDurationOptions.map((duration) => (
                      <option key={duration} value={duration}>
                        {duration === 0 ? "No break" : `${duration} Min`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={applyBulkEdit}
                    disabled={selectedRows.length === 0}
                    className="flex-1 px-4 py-2 bg-card border border-input text-muted-foreground rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold shadow-sm transition-colors"
                  >
                    Apply
                  </button>
                  {selectedRows.length > 0 && (
                    <button
                      onClick={deleteSelectedRows}
                      className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm font-medium"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 3. Schedule List (Table View) */}
          {scheduleList.length > 0 && (
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
              <div className="overflow-x-hidden">
                <table className="w-full table-fixed text-sm text-left text-muted-foreground">
                  <thead className="text-xs text-foreground uppercase bg-muted border-b border-border">
                    <tr>
                      <th className="p-4 w-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-muted border-input rounded focus:ring-blue-500"
                            checked={
                              scheduleList.length > 0 &&
                              selectedRows.length === scheduleList.length
                            }
                            onChange={handleSelectAll}
                          />
                        </div>
                      </th>
                      <th className="px-3 py-3 w-[18%]">Date</th>
                      <th className="px-3 py-3 w-[11%]">From Time</th>
                      <th className="px-3 py-3 w-[11%]">To Time</th>
                      <th className="px-3 py-3 w-[14%]">Interview Type</th>
                      <th className="px-3 py-3 w-[12%]">Slot Duration</th>
                      <th className="px-3 py-3 w-[12%]">Break Duration</th>
                      <th className="px-3 py-3 w-[10%]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleList.map((schedule, index) => (
                      <tr
                        key={schedule.id || index}
                        className="bg-card border-b hover:bg-muted"
                      >
                        <td className="p-4 w-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-blue-600 bg-muted border-input rounded focus:ring-blue-500"
                              checked={selectedRows.includes(schedule.id)}
                              onChange={() => handleSelectRow(schedule.id)}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-4 font-medium text-foreground">
                          {formatDate(schedule.date)}
                        </td>
                        <td className="px-3 py-4">
                          <input
                            type="time"
                            value={schedule.fromTime}
                            onChange={(e) =>
                              handleScheduleChange(
                                index,
                                "fromTime",
                                e.target.value,
                              )
                            }
                            className="bg-muted border border-input text-foreground text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2"
                          />
                        </td>
                        <td className="px-3 py-4">
                          <input
                            type="time"
                            value={schedule.toTime}
                            onChange={(e) =>
                              handleScheduleChange(
                                index,
                                "toTime",
                                e.target.value,
                              )
                            }
                            min={schedule.fromTime}
                            className="bg-muted border border-input text-foreground text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2"
                          />
                        </td>
                        <td className="px-3 py-4">
                          <select
                            value={schedule.interviewType || ""}
                            onChange={(e) =>
                              handleScheduleChange(
                                index,
                                "interviewType",
                                e.target.value,
                              )
                            }
                            className="bg-muted border border-input text-foreground text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2"
                          >
                            <option value="">Select</option>
                            {interviewTypeOptions.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-4">
                          <select
                            value={schedule.slotDuration}
                            onChange={(e) =>
                              handleScheduleChange(
                                index,
                                "slotDuration",
                                e.target.value,
                              )
                            }
                            className="bg-muted border border-input text-foreground text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2"
                          >
                            <option value="">Select</option>
                            {slotDurationOptions.map((duration) => (
                              <option key={duration} value={duration}>
                                {duration} Min
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-4">
                          <select
                            value={schedule.breakDuration}
                            onChange={(e) =>
                              handleScheduleChange(
                                index,
                                "breakDuration",
                                e.target.value,
                              )
                            }
                            className="bg-muted border border-input text-foreground text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2"
                          >
                            <option value="">Select</option>
                            {breakDurationOptions.map((duration) => (
                              <option key={duration} value={duration}>
                                {duration === 0 ? "No break" : `${duration} Min`}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-4">
                          <button
                            onClick={() => removeSchedule(index)}
                            className="font-medium text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* 2/4. Add Date Section (Allows appending more) */}
        <div className=" py-2">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {scheduleList.length > 0 ? "Add More Dates" : "Select Dates"}
          </h3>
          <div className="flex flex-col md:flex-row gap-4 items-start">
            <div className="w-full  md:w-auto">
              {/* <label className="block text-sm font-medium text-foreground mb-2">
                                Date Range
                            </label> */}
              <DatePicker
                selected={selectedDateRange[0]}
                startDate={selectedDateRange[0]}
                endDate={selectedDateRange[1]}
                dateFormat={["dd/MM/yyyy", "dd/MM/yyyy"]}
                selectsRange
                onChange={handleDateRangeSelect}
                customInput={
                  <button
                    type="button"
                    className="relative w-full min-w-[230px] h-[34px]  rounded-xl border border-border bg-card px-4  text-sm text-foreground text-left outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <span
                      className={
                        dateRangeLabel ? "text-foreground" : "text-muted-foreground"
                      }
                    >
                      {dateRangeLabel || "Select start and end date"}
                    </span>
                    <Calendar className="w-[18px] h-[18px] text-muted-foreground absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </button>
                }
              />
            </div>
            <button
              onClick={handleCreateAvailability}
              className="bg-primary-dark hover:bg-black/90 text-white px-4 py-1.5 rounded-lg font-medium transition-colors shadow-sm whitespace-nowrap"
            >
              Create Availability
            </button>
          </div>
        </div>

        <div className="pt-6">
          <button
            onClick={handleCreateSlot}
            disabled={isCreatingSlot}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary-dark text-white rounded-lg hover:bg-black/90 transition-colors font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreatingSlot ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating Slots...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Create Slots
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEventSlot;
